import { Component, type ReactNode } from "react";
import { isChunkLoadError, reloadForChunkError } from "@/lib/chunkReload";

type Props = { children: ReactNode };
type State = { hasError: boolean; isChunkError: boolean };

// Catches errors thrown while rendering the lazy-loaded route tree. When the
// cause is a stale JS chunk (post-deploy cache mismatch) it forces a single
// hard reload to pick up the current chunk graph; if a reload has already been
// attempted this session it shows a manual "reload" fallback instead of looping.
// Non-chunk render errors fall through to a generic reloadable message and are
// forwarded to Sentry.
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: unknown, info: unknown) {
    if (isChunkLoadError(error)) {
      // Reload once to fetch the fresh bundle. If we already reloaded this
      // session, fall through to the manual fallback rendered below.
      reloadForChunkError();
      return;
    }
    void import("@sentry/react")
      .then((Sentry) => {
        if (Sentry.getClient()) {
          Sentry.captureException(error, { extra: { componentStack: info } });
        }
      })
      .catch(() => {
        /* Sentry unavailable — nothing else to do */
      });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 min-h-[60vh] px-6 text-center">
          <p className="text-base" style={{ color: "var(--gbz-muted-fg)" }}>
            {this.state.isChunkError
              ? "A new version of the app is available."
              : "Something went wrong."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md px-4 py-2 text-sm font-semibold"
            style={{ backgroundColor: "var(--gbz-accent)", color: "var(--gbz-accent-ink)" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
